import React, { useState, useCallback, useEffect, useRef, Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import useSmoothScroll from './hooks/useSmoothScroll';

import Nav from './components/Nav';
import Footer from './components/Footer';

import Home from './pages/Home';
import About from './pages/About';
import ReviveNights from './pages/ReviveNights';
import WhatWeBelieve from './pages/WhatWeBelieve';
import FAQPage from './pages/FAQPage';
import ThankYou from './pages/ThankYou';
import DonorPortal from './pages/DonorPortal';
import NotFound from './pages/NotFound';
import useSiteConfig from './hooks/useSiteConfig';
import GiveModal from './components/GiveModal';
import JoinModal from './components/JoinModal';
import NewsletterModal from './components/NewsletterModal';
import MobileGiveBar from './components/MobileGiveBar';
import YearInReviewPromo from './components/YearInReviewPromo';

// The whole admin app is code-split into its own chunk so public visitors
// never download it. It loads only when someone opens /admin.
const Admin = lazy(() => import('./pages/Admin'));

// Year in Review launch promo: shows once per visitor until this date,
// and never interrupts the giving / serving / receipt flows.
const YIR_PROMO_ENDS = '2026-08-14T00:00:00';
const YIR_PROMO_KEY = 'rw-yir-promo';
const YIR_PROMO_SKIP = ['/give', '/serve', '/thank-you', '/giving', '/admin'];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function Shell() {
  useSmoothScroll();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { config } = useSiteConfig();

  const [giveOpen, setGiveOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);

  // Live view of "is anything else open?" for the promo timer below —
  // a ref so the mount-only effect never reads stale state.
  const overlayBusyRef = useRef(false);

  // Decide once, on first mount only, so route changes never re-trigger it.
  useEffect(() => {
    if (Date.now() >= new Date(YIR_PROMO_ENDS).getTime()) return;
    try {
      if (window.localStorage.getItem(YIR_PROMO_KEY)) return;
    } catch (e) { return; }
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (YIR_PROMO_SKIP.includes(path)) return;
    const t = setTimeout(() => {
      // If the visitor already opened Give/Serve/Newsletter or the menu,
      // stay out of the way; they'll see the promo on their next visit.
      if (overlayBusyRef.current) return;
      setPromoOpen(true);
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const closePromo = useCallback(() => {
    setPromoOpen(false);
    try { window.localStorage.setItem(YIR_PROMO_KEY, '1'); } catch (e) {}
  }, []);

  // Dedicated entry links: reviveworship.us/give and /serve render Home
  // with the matching flow already open — shareable from bios, QR codes,
  // and print, alongside the in-page buttons.
  const giveRoute = pathname === '/give';
  const serveRoute = pathname === '/serve';
  const showGive = giveOpen || giveRoute;
  const showJoin = joinOpen || serveRoute;

  // Keep the promo's overlay guard current on every render.
  useEffect(() => {
    overlayBusyRef.current = showGive || showJoin || newsletterOpen || menuOpen;
  });

  const openGive = useCallback(() => setGiveOpen(true), []);
  const openJoin = useCallback(() => setJoinOpen(true), []);
  const openNewsletter = useCallback(() => setNewsletterOpen(true), []);

  const closeGive = useCallback(() => {
    setGiveOpen(false);
    if (giveRoute) navigate('/', { replace: true });
  }, [giveRoute, navigate]);

  const closeJoin = useCallback(() => {
    setJoinOpen(false);
    if (serveRoute) navigate('/', { replace: true });
  }, [serveRoute, navigate]);

  useEffect(() => {
    if (!giveRoute && !serveRoute) return;
    const prev = document.title;
    document.title = giveRoute
      ? 'Give — Revive Worship'
      : 'Serve — Revive Worship';
    return () => { document.title = prev; };
  }, [giveRoute, serveRoute]);

  // The admin panel is its own quiet surface: no site chrome, no modals.
  // It owns every /admin and /admin/* route (dashboard-cards sub-pages), so
  // deep links and refresh land in place. (This return sits below every
  // hook, so hook order is stable.)
  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<div style={{ minHeight: '100svh' }} />}>
          <Admin />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="grain" aria-hidden="true" />

      <Nav onGive={openGive} menuOpen={menuOpen} setMenuOpen={setMenuOpen} links={config.links} />

      <main>
        <Routes>
          <Route path="/" element={<Home onNewsletter={openNewsletter} />} />
          <Route path="/give" element={<Home onNewsletter={openNewsletter} />} />
          <Route path="/serve" element={<Home onNewsletter={openNewsletter} />} />
          <Route path="/about" element={<About />} />
          <Route path="/nights" element={<ReviveNights />} />
          <Route path="/believe" element={<WhatWeBelieve />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/giving" element={<DonorPortal />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <Footer onGive={openGive} onJoin={openJoin} onNewsletter={openNewsletter} links={config.links} />

      <MobileGiveBar onGive={openGive} hidden={showGive || menuOpen || promoOpen} />

      {showGive && <GiveModal onClose={closeGive} />}
      {showJoin && <JoinModal onClose={closeJoin} />}
      {newsletterOpen && (
        <NewsletterModal onClose={() => setNewsletterOpen(false)} />
      )}
      {/* Never render the promo over another overlay, whichever opened first.
          If it was armed while something else was up, it waits its turn.
          The admin toggle (config.promo) can also switch it off site-wide. */}
      {promoOpen && config.promo.enabled &&
        Date.now() < new Date(config.promo.endsAt).getTime() &&
        !showGive && !showJoin && !newsletterOpen && !menuOpen && (
        <YearInReviewPromo onClose={closePromo} />
      )}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
